import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { RouterHistory } from 'react-router';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Swal from 'sweetalert2';
import { AppUser, withAppUser } from '../../shared/authorization';
import { compose, formatDate } from '../../utility';
import withFade from '../../account/shared/animationHOC';
import { withSnackbar } from '../../shared/snackbar';
import SpinLoader from '../../util/SpinLoader';
import useRequest from '../../util/RequestEsign';
import { FEE_AMOUNT } from '../../configurations';
import PaymentMethodSection from './PaymentMethodSection';

type Props = {
    history: RouterHistory,
    user: AppUser,
    createSnackbar: Function,
    classes: Object,
};

const PRIMARY = '#0f62bd';
const PRIMARY_DARK = '#0a4a91';
const TEXT_MAIN = '#1a1f36';
const TEXT_SECONDARY = '#4a5568';
const BORDER = '#e2e8f0';
const BG = '#f7f9fc';

function formatTransactionMethod(method) {
  if (method == null || String(method).trim() === '') {
    return '—';
  }
  const s = String(method).trim();
  if (/^creditcard$/i.test(s)) {
    return 'Card';
  }
  if (/^eft$/i.test(s)) {
    return 'EFT';
  }
  return s;
}

function formatTransactionStatus(status) {
  if (status == null || String(status).trim() === '') {
    return '—';
  }
  const s = String(status).trim();
  if (/^inprogress$/i.test(s)) {
    return 'In progress';
  }
  if (/^completed$/i.test(s)) {
    return 'Completed';
  }
  if (/^failed$/i.test(s)) {
    return 'Failed';
  }
  return s;
}

/** True when validTo is strictly after local calendar today (subscription end date in the future). */
function subscriptionValidToAfterToday(validTo) {
  if (validTo == null || String(validTo).trim() === '') {
    return false;
  }
  const s = String(validTo).trim();
  if (s === '0001-01-01T00:00:00' || s === '0001-01-01 00:00:00') {
    return false;
  }
  const end = new Date(s);
  if (Number.isNaN(end.getTime())) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return end > today;
}

const styles = () => ({
  page: {
    backgroundColor: BG,
    padding: '40px 0 80px',
    fontFamily: 'Work Sans, Helvetica, Arial, sans-serif',
  },
  inner: {
    maxWidth: '100%',
    margin: '0 auto',
  },

  // ─── Page header ───────────────────────────────────────────────────
  pageHeader: {
    marginBottom: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  pageTitle: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: TEXT_MAIN,
    margin: 0,
  },
  pageHeaderLeft: {
    flex: '1 1 280px',
    minWidth: 0,
  },
  backBtn: {
    textTransform: 'none',
    fontWeight: 600,
    color: TEXT_SECONDARY,
    fontSize: '0.9rem',
    '&:hover': { color: PRIMARY },
  },

  // ─── Card shell ────────────────────────────────────────────────────
  card: {
    backgroundColor: '#fff',
    borderRadius: 14,
    border: `1px solid ${BORDER}`,
    boxShadow: '0 2px 16px rgba(15,98,189,0.06)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '20px 28px',
    borderBottom: `1px solid ${BORDER}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_MAIN,
    margin: 0,
  },
  cardBody: {
    padding: '24px 28px',
  },

  // ─── Status badge ──────────────────────────────────────────────────
  badgeSubscribed: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#065f46',
    backgroundColor: '#d1fae5',
    borderRadius: 20,
    padding: '3px 12px',
  },
  badgeUnsubscribed: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#92400e',
    backgroundColor: '#fef3c7',
    borderRadius: 20,
    padding: '3px 12px',
  },

  // ─── Stats grid ────────────────────────────────────────────────────
  statsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -12,
    marginRight: -12,
    marginBottom: 28,
    '& > *': {
      flex: '1 1 140px',
      margin: 12,
    },
  },
  statItem: {
    backgroundColor: BG,
    borderRadius: 10,
    padding: '16px 20px',
    border: `1px solid ${BORDER}`,
  },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  },
  statValue: {
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_MAIN,
  },

  // ─── Action buttons ────────────────────────────────────────────────
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    paddingTop: 4,
    '& > *:not(:first-child)': {
      marginLeft: 12,
    },
  },
  btnPrimary: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    borderRadius: 8,
    padding: '9px 20px',
    '&:hover': { backgroundColor: PRIMARY_DARK },
  },
  btnOutlined: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: TEXT_MAIN,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: '9px 20px',
    '&:hover': { borderColor: PRIMARY, color: PRIMARY },
  },

  // ─── Empty state ───────────────────────────────────────────────────
  emptyState: {
    padding: '32px 0',
    textAlign: 'center',
    color: TEXT_SECONDARY,
    fontSize: '0.9rem',
  },
  emptyDot: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#eef3fb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
    fontSize: '1.4rem',
    color: '#93c5fd',
  },

  // ─── Transactions ──────────────────────────────────────────────────
  txTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  txHead: {
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: TEXT_SECONDARY,
    padding: '0 0 12px',
    textAlign: 'left',
    borderBottom: `2px solid ${BORDER}`,
  },
  txCell: {
    fontSize: '0.9rem',
    color: TEXT_MAIN,
    padding: '14px 0',
    borderBottom: `1px solid ${BORDER}`,
    verticalAlign: 'middle',
  },
  txAmount: {
    fontWeight: 600,
    color: PRIMARY,
    textAlign: 'right',
  },
  txDate: {
    color: TEXT_SECONDARY,
  },
  txMethod: {
    textAlign: 'center',
    color: TEXT_SECONDARY,
    fontWeight: 500,
    paddingLeft: 8,
    paddingRight: 8,
  },
  txStatus: {
    textAlign: 'center',
    color: TEXT_SECONDARY,
    fontWeight: 500,
    paddingLeft: 8,
    paddingRight: 8,
  },
  loaderWrap: {
    padding: '32px 0',
    textAlign: 'center',
  },
});

const withTimeout = (promise, ms) => new Promise((resolve, reject) => {
  const t = setTimeout(() => reject(new Error('timeout')), ms);
  promise
    .then((v) => { clearTimeout(t); resolve(v); })
    .catch((e) => { clearTimeout(t); reject(e); });
});

const PaymentInfo = (props: Props) => {
  const { classes } = props;

  const [subscription, setSubscription] = useState({
    amount: '0',
    billingFrequency: 'Yearly',
    source: 0,
    status: 0,
    validFrom: '0001-01-01 00:00:00',
    validTo: '0001-01-01 00:00:00',
  });
  const [payments, setPayments] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [payMethods, setPayMethods] = useState({ hasCard: false, hasBank: false });

  const getSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const jsonResponse = await useRequest({ endPoint: '/subscription' });
      setSubscription(jsonResponse);
    } catch (error) {
      // ignore
    }
    setSubscriptionLoading(false);
  }, []);

  const getPayments = useCallback(async (options) => {
    const opts = options || {};
    const pollAttempts = opts.pollAttempts || 1;
    const pollDelayMs = opts.pollDelayMs || 700;
    const silent = !!opts.silent;
    if (!silent) {
      setPaymentLoading(true);
    }
    const sleep = ms => new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
    const fetchPaymentsOnce = async () => {
      try {
        const paymentsRequest = await useRequest({ endPoint: '/payment' });
        setPayments(paymentsRequest.data);
      } catch (error) {
        // ignore
      }
    };
    const pollAttempt = async (attempt) => {
      await fetchPaymentsOnce();
      if (attempt + 1 < pollAttempts) {
        await sleep(pollDelayMs);
        await pollAttempt(attempt + 1);
      }
    };
    try {
      await pollAttempt(0);
    } finally {
      if (!silent) {
        setPaymentLoading(false);
      }
    }
  }, []);

  const { hasCard, hasBank } = payMethods;

  const handlePayment = async () => {
    if (!hasCard && !hasBank) {
      Swal.fire({
        title: 'Add a payment method',
        html: '<i>Add a credit card or link a bank account (EFT) below to subscribe.</i>',
        icon: 'info',
      });
      return;
    }

    const payMethodSelectHtml = [
      '<div style="margin-top:12px;text-align:left;max-width:100%;box-sizing:border-box">',
      '<label for="payMethodSelect" style="display:block;margin-bottom:6px;font-weight:600">',
      'Pay with</label>',
      '<select id="payMethodSelect" style="width:100%;max-width:100%;box-sizing:border-box;',
      'padding:8px;border-radius:6px;border:1px solid #ccc">',
      '<option value="CreditCard">Credit card</option>',
      '<option value="Eft">Bank account (EFT)</option></select></div>',
    ].join('');
    const methodSelectHtml = hasCard && hasBank ? payMethodSelectHtml : '';
    const extendLabel = subscriptionValidToAfterToday(subscription.validTo);

    Swal.fire({
      title: `${extendLabel ? 'Extend' : 'Buy'} subscription`,
      html: `<p>Your account will be charged <strong>${FEE_AMOUNT} CAD</strong>.</p>${methodSelectHtml}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          let payBody;
          if (hasCard && hasBank) {
            const sel = Swal.getPopup().querySelector('#payMethodSelect');
            payBody = { paymentMethod: sel ? sel.value : 'CreditCard' };
          }
          const reqOpts = {
            endPoint: '/payment',
            method: 'POST',
            returnStatus: true,
          };
          if (payBody) {
            reqOpts.body = payBody;
          }
          const res = await withTimeout(useRequest(reqOpts), 20000);
          if (!res.ok) {
            throw new Error('request_failed');
          }
          return res;
        } catch (e) {
          Swal.showValidationMessage('Something went wrong. Please contact your admin.');
          return null;
        }
      },
    }).then((result) => {
      if (!result.isConfirmed || !result.value) {
        return;
      }
      const { status, data: payRes } = result.value;
      if (status === 200 && payRes && payRes.validTo) {
        setSubscription({
          ...subscription,
          validTo: payRes.validTo,
          validFrom: payRes.validFrom,
          status: 1,
        });
      }
      getSubscription();
      // EFT transactions can take longer to settle; poll longer for 201 (in-progress) responses.
      const pollAttempts = status === 201 ? 90 : 12;
      getPayments({
        pollAttempts,
        pollDelayMs: status === 201 ? 2000 : 800,
        silent: true,
      });
      const inProgress = status === 201;
      Swal.fire({
        icon: 'success',
        title: inProgress ? 'Payment in progress' : 'Payment processed',
        text: inProgress ? 'Transaction history will update when the charge completes.' : undefined,
        toast: true,
        showConfirmButton: false,
        timer: inProgress ? 3200 : 2200,
      });
    }).catch(() => {});
  };

  useEffect(() => {
    getSubscription();
    getPayments();
  }, [getSubscription, getPayments]);

  const isSubscribed = subscription.status === 1;
  const extendSubscriptionCta = subscriptionValidToAfterToday(subscription.validTo);
  return (
    <div className={classes.page}>
      <div className={classes.inner}>

        {/* ─── Header ──────────────────────────────────────── */}
        <div className={classes.pageHeader}>
          <div className={classes.pageHeaderLeft}>
            <h1 className={classes.pageTitle}>Billing &amp; Subscription</h1>
          </div>
          <Button className={classes.backBtn} onClick={() => props.history.push('/agent/sessions')}>
            &larr; Back to sessions
          </Button>
        </div>

        {/* ─── Subscription card ───────────────────────────── */}
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <h2 className={classes.cardTitle}>Subscription</h2>
            {!subscriptionLoading && (
              <span className={isSubscribed ? classes.badgeSubscribed : classes.badgeUnsubscribed}>
                {isSubscribed ? 'Active' : 'Inactive'}
              </span>
            )}
          </div>
          <div className={classes.cardBody}>
            {subscriptionLoading ? (
              <div className={classes.loaderWrap}><SpinLoader /></div>
            ) : (
              <Fragment>
                <div className={classes.statsGrid}>
                  <div className={classes.statItem}>
                    <div className={classes.statLabel}>Plan</div>
                    <div className={classes.statValue}>Yearly</div>
                  </div>
                  <div className={classes.statItem}>
                    <div className={classes.statLabel}>Amount</div>
                    <div className={classes.statValue}>
                      $
                      {subscription.amount || FEE_AMOUNT}
                      {' '}
                      CAD
                    </div>
                  </div>
                  <div className={classes.statItem}>
                    <div className={classes.statLabel}>Valid from</div>
                    <div className={classes.statValue}>{formatDate(subscription.validFrom)}</div>
                  </div>
                  <div className={classes.statItem}>
                    <div className={classes.statLabel}>Valid to</div>
                    <div className={classes.statValue}>{formatDate(subscription.validTo)}</div>
                  </div>
                </div>
                <div className={classes.actions}>
                  <Button variant="contained" className={classes.btnPrimary} onClick={handlePayment}>
                    {extendSubscriptionCta ? 'Extend' : 'Buy'}
                    {' '}
                    subscription ($
                    {FEE_AMOUNT}
                    {' '}
                    CAD)
                  </Button>
                </div>
              </Fragment>
            )}
          </div>
        </div>

        <PaymentMethodSection
          history={props.history}
          user={props.user}
          createSnackbar={props.createSnackbar}
          onPaymentStateChange={setPayMethods}
        />

        {/* ─── Transaction history card ─────────────────────── */}
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <h2 className={classes.cardTitle}>Transaction History</h2>
          </div>
          <div className={classes.cardBody}>
            {(() => {
              if (paymentLoading) {
                return <div className={classes.loaderWrap}><SpinLoader /></div>;
              }
              if (payments && payments.length > 0) {
                return (
                  <table className={classes.txTable}>
                    <thead>
                      <tr>
                        <th className={classes.txHead}>Date</th>
                        <th className={`${classes.txHead} ${classes.txMethod}`}>Method</th>
                        <th className={`${classes.txHead} ${classes.txStatus}`}>Status</th>
                        <th className={`${classes.txHead} ${classes.txAmount}`}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(row => (
                        <tr key={`${row.date}-${row.amount}-${row.method || ''}-${row.status || ''}`}>
                          <td className={`${classes.txCell} ${classes.txDate}`}>{formatDate(row.date)}</td>
                          <td className={`${classes.txCell} ${classes.txMethod}`}>
                            {formatTransactionMethod(row.method)}
                          </td>
                          <td className={`${classes.txCell} ${classes.txStatus}`}>
                            {formatTransactionStatus(row.status)}
                          </td>
                          <td className={`${classes.txCell} ${classes.txAmount}`}>
                            $
                            {row.amount}
                            {' '}
                            CAD
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              }
              return (
                <div className={classes.emptyState}>
                  <div className={classes.emptyDot}>&#9776;</div>
                  <p>No transactions yet.</p>
                </div>
              );
            })()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default compose(
  withFade,
  withSnackbar,
  withAppUser,
  withRouter,
  withStyles(styles),
)(PaymentInfo);
